import type { PartnerTenant, PartnerTenantCreate, PartnerTenantUpdate } from '@shipium-corp/shipium-ts-types';
import type { FetchAssetParams } from '~/store/assets/types';
import GatewayAxiosClient from '../../store/services/GatewayAxiosClient';
import { type LinkAssetPayload, type BulkLinkPayloads, linkMultipleAssets } from '../api-utils';

type SingleAssetLinkType = 'usppiAddresses' | 'usppiContacts';
type ArrayAssetLinkType = 'tags';

export const tenants = {
  search: {
    queryKey: 'partner-management-service/tenants',
    configurationType: 'default' as const,
    fn: async (params: FetchAssetParams = {}) => {
      const { data } = await GatewayAxiosClient.get<PartnerTenant[]>(
        '/partner-management-proxy/api/v1/partners/tenants',
        { params },
      );

      return data;
    },
  },
  get: {
    queryKey: 'partner-management-service/tenants/id',
    configurationType: 'no-cache' as const,
    fn: async (tenantId: string) => {
      const { data } = await GatewayAxiosClient.get<PartnerTenant>(
        `/partner-management-proxy/api/v1/partners/tenants/${tenantId}`,
      );

      return data;
    },
  },
  create: async (tenant: PartnerTenantCreate) => {
    const { data } = await GatewayAxiosClient.post<PartnerTenant>(
      '/partner-management-proxy/api/v1/partners/tenants',
      tenant,
    );

    return { data, invalidateQueryKeys: [['partner-management-service/tenants']] };
  },
  update: async ({ tenantId, tenant }: { tenantId: string; tenant: PartnerTenantUpdate }) => {
    const { data } = await GatewayAxiosClient.put<PartnerTenant>(
      `/partner-management-proxy/api/v1/partners/tenants/${tenantId}`,
      tenant,
    );

    return {
      data,
      invalidateQueryKeys: [
        ['partner-management-service/tenants'],
        ['partner-management-service/tenants/id', tenantId],
      ],
    };
  },
  link: async ({
    tenantId,
    payload,
  }: {
    tenantId: string;
    payload: LinkAssetPayload<SingleAssetLinkType | ArrayAssetLinkType>;
  }) => {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url: `/partner-management-proxy/api/v1/partners/tenants/${tenantId}/${payload.assetType}/${payload.assetId}`,
    });

    return {
      invalidateQueryKeys: [
        ['partner-management-service/tenants'],
        ['partner-management-service/tenants/id', tenantId],
      ],
    };
  },
  bulkLink: async ({
    tenant,
    payloads,
  }: {
    tenant: PartnerTenant;
    payloads: BulkLinkPayloads<SingleAssetLinkType, ArrayAssetLinkType>;
  }) => {
    return await linkMultipleAssets<SingleAssetLinkType, ArrayAssetLinkType, PartnerTenant>(
      payloads,
      {
        usppiAddresses: tenant.unitedStatesPrincipalPartyInInterestAddressId,
        usppiContacts: tenant.unitedStatesPrincipalPartyInInterestContactId,
        tags: tenant.tagIds,
      },
      (payload) => tenants.link({ tenantId: tenant.tenantId!, payload }),
    );
  },
};
